import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { DEFAULT_PAGE_SIZE } from "@/shared/constants/pagination";

import * as mineApi from "./api";
import type { GetDeletedItemsData, GetItemsData, ItemSortBy, ItemSortOrder } from "./types";
import type { WorkspaceDisplayParams } from "./workspace/types";

export interface MineItemsQueryFilters {
  workspace: WorkspaceDisplayParams;
  sortBy?: ItemSortBy;
  sortOrder?: ItemSortOrder;
}

export interface MineItemsPageQueryFilters extends MineItemsQueryFilters {
  page?: number;
}

type MineItemsQueryKeyFilters = Omit<GetItemsData, "page" | "limit">;

export function normalizeMineItemsQueryFilters(
  filters: MineItemsQueryFilters,
): MineItemsQueryKeyFilters {
  const { workspace, sortBy = "id", sortOrder = "desc" } = filters;
  const params: MineItemsQueryKeyFilters = {
    order: sortBy,
    sort: sortOrder,
  };

  if (workspace.duplicatesOnly) {
    params.duplicates_only = true;
  }

  if (workspace.format && workspace.format !== "all") {
    params.format = workspace.format;
  }

  if (workspace.keyword?.trim()) {
    params.q = workspace.keyword.trim();
  }

  if (workspace.type === "folder") {
    if (!workspace.folder_id) {
      throw new Error("folder_id is required");
    }

    params.folder_id = workspace.folder_id;
    return params;
  }

  if (workspace.type === "tag") {
    if (!workspace.tagId) {
      throw new Error("tagId is required");
    }

    params.tag_id = workspace.tagId;
    return params;
  }

  if (workspace.folder_id) {
    params.folder_id = workspace.folder_id;
  }

  return params;
}

export function buildMineItemsParams(
  filters: MineItemsPageQueryFilters,
): GetItemsData {
  return {
    page: filters.page ?? 1,
    limit: DEFAULT_PAGE_SIZE,
    ...normalizeMineItemsQueryFilters(filters),
  };
}

export function buildDeletedItemsParams(
  data?: GetDeletedItemsData,
): GetDeletedItemsData {
  return {
    page: data?.page ?? 1,
    limit: data?.limit ?? DEFAULT_PAGE_SIZE,
    ...data,
  };
}

export const mineQueryKeys = {
  items: ["mine", "items"] as const,
  itemsList: (filters: MineItemsQueryFilters) =>
    ["mine", "items", normalizeMineItemsQueryFilters(filters)] as const,
  itemDetail: (id: string) => ["mine", "items", id] as const,
  itemAttachments: (id: string) => ["mine", "items", id, "attachments"] as const,
  deletedItems: ["mine", "deleted-items"] as const,
  deletedItemsList: (filters?: Omit<GetDeletedItemsData, "page" | "limit">) =>
    ["mine", "deleted-items", filters ?? {}] as const,
  folders: ["mine", "folders"] as const,
  tags: ["mine", "tags"] as const,
} as const;

export function getItemsListQueryOptions(filters: MineItemsQueryFilters) {
  return queryOptions({
    queryKey: mineQueryKeys.itemsList(filters),
    queryFn: () => mineApi.getItems(buildMineItemsParams(filters), { silent: true }),
  });
}

export function getItemsInfiniteQueryOptions(filters: MineItemsQueryFilters) {
  return infiniteQueryOptions({
    queryKey: mineQueryKeys.itemsList(filters),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      mineApi.getItems(
        buildMineItemsParams({
          ...filters,
          page: pageParam,
        }),
        { silent: true },
      ),
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.items.length === DEFAULT_PAGE_SIZE
        ? lastPageParam + 1
        : undefined,
  });
}

export function getDeletedItemsInfiniteQueryOptions(
  filters?: Omit<GetDeletedItemsData, "page" | "limit">,
) {
  return infiniteQueryOptions({
    queryKey: mineQueryKeys.deletedItemsList(filters),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      mineApi.getDeletedItems(
        buildDeletedItemsParams({
          ...filters,
          page: pageParam,
        }),
        { silent: true },
      ),
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.items.length === DEFAULT_PAGE_SIZE
        ? lastPageParam + 1
        : undefined,
  });
}

export function getFoldersQueryOptions() {
  return queryOptions({
    queryKey: mineQueryKeys.folders,
    queryFn: () => mineApi.getFolders(),
  });
}

export function getTagsQueryOptions() {
  return queryOptions({
    queryKey: mineQueryKeys.tags,
    queryFn: () => mineApi.getTags(),
  });
}
